import React, {Dispatch, FC, useCallback, useEffect, useState} from 'react';
import {Alert, Button, Col, Form, Modal, Row} from "react-bootstrap";
import {FaExpandAlt} from "react-icons/fa";
import ObjectService from "../../http/ObjectService";
import Loading from "../Loading";
import {FaRegTrashCan} from "react-icons/fa6";

interface PropsModal {
    visible: boolean;
    setVisible: Dispatch<boolean>;
    objectId: string | number | null;
    refreshTable: () => void;
    categories: any;
}

const ObjectModalEdite: FC<PropsModal> = (props) => {
    const [object, setObject] = useState<any | null>(null);
    const [categoryId, setCategoryId] = useState<any | null>(null);

    const [district, setDistrict] = useState<string>("");
    const [address, setAddress] = useState<string>("");
    const [name, setName] = useState<string>("");
    const [comment, setComment] = useState<string>("");
    const [images, setImages] = useState<any[]>([]);
    const [imagesInfo, setImagesInfo] = useState<any[]>([]);
    const [imagesDelete, setImagesDelete] = useState<any[]>([]);

    const [error, setError] = useState<string | null>(null);
    const [errorFiles, setErrorFiles] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [warning, setWarning] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const [validated, setValidated] = useState(false);
    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);

    const fetchObject = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await ObjectService.query({
                includes: ["categories", "photos"],
                options: { where: { id: props.objectId } }
            });

            if (data && data.length > 0) {
                const objectData = data[0];
                if (objectData.action === "DELETE") {
                    setError('Объект был удалён');
                    setNeedsRefresh(true);
                }

                setDistrict(objectData.district || "");
                setAddress(objectData.address || "");
                setName(objectData.name || "");
                setComment(objectData.comment || "");
                setCategoryId(objectData.category.id);
                setObject(objectData);
            } else {
                setNeedsRefresh(true);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [props.objectId]);


    useEffect(() => {
        if (props.visible) {
            void fetchObject();
        }
    }, [props.visible, fetchObject]);



    const handleSubmitEdite = async (event: React.FormEvent<HTMLFormElement>) => {
        const form = event.currentTarget;
        event.preventDefault();

        if (!form.checkValidity()) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        const formData = new FormData();
        formData.append('id', String(object.id));
        formData.append('district', district);
        formData.append('address', address);
        formData.append('name', name);
        formData.append('comment', comment);
        formData.append('imagesInfo', JSON.stringify(imagesInfo));
        formData.append('imagesDelete', JSON.stringify(imagesDelete));
        images.forEach(image => formData.append('images', image));

        try {
            const { data } = await ObjectService.update(formData);
            if (data) {
                setSuccess("Объект изменился");
                setError(null);
                setNeedsRefresh(true);
                setImages([]);
                setImagesInfo([]);
                setImagesDelete([]);
            } else {
                setWarning("Объект не изменился");
            }
            await fetchObject();
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        }
    };

    const handleSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        setErrorFiles(null);
        const targetFiles = event.target.files;
        if (!targetFiles) return;

        const currentPhotosCount = object?.photos?.length || 0;
        if (targetFiles.length + currentPhotosCount > 10) {
            setErrorFiles("Максимальное количество фотографий — 10");
            return;
        }

        const filesArray = Array.from(targetFiles);
        setImages(filesArray);
        setImagesInfo([]);

        filesArray.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const img = new Image();
                img.onload = () => {
                    setImagesInfo(prev => [...prev, {
                        name: file.name,
                        width: img.width,
                        height: img.height,
                    }]);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    const handleDeleteExistingImage = (imageId: number | string) => {
        const photoToDelete = object.photos.find((p: any) => p.id === imageId);
        if (photoToDelete) {
            setImagesDelete(prev => [...prev, photoToDelete]);
            setObject({
                ...object,
                photos: object.photos.filter((p: any) => p.id !== imageId)
            });
        }
    };

    const handleHideModal = () => {
        if (needsRefresh) props.refreshTable();
        props.setVisible(false);
    };


    return (
        <Modal show={props.visible} size={"xl"} fullscreen={fullscreen ? true : "md-down"} scrollable={true}
               onHide={handleHideModal}>
            <div className="modal-header">
                <h5 className="mb-0">Редактирование объекта</h5>
                <div className={"d-flex gap-3"}>
                    <button type="button" className="btn-link" onClick={() => setFullscreen(!fullscreen)}>
                        <FaExpandAlt/>
                    </button>
                    <button type="button" className="btn-close" onClick={handleHideModal}></button>
                </div>
            </div>
            {loading
                ?
                <div style={{height: "200px"}}><Loading/></div>
                :
                <>
                    {object
                        ?
                        <Form noValidate validated={validated} onSubmit={handleSubmitEdite}>
                            <Modal.Body>
                                {success && <Alert variant={"success"} dismissible={true}>{success}</Alert>}
                                {warning && <Alert variant={"warning"} dismissible={true}>{warning}</Alert>}
                                {error && <Alert variant={"danger"} dismissible={true}>{error}</Alert>}


                                {object.action !== "DELETE" && (
                                    <>
                                        <Form.Group as={Row} className="mb-3" controlId="category">
                                            <Form.Label column sm="2">Категория <span
                                                className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <Form.Select required={true} isInvalid={!categoryId}
                                                             defaultValue={categoryId}
                                                             onChange={(e) => setCategoryId(e.target.value)}>
                                                    <option value={""}>-- Выберите --</option>
                                                    {props.categories && props.categories.map((cat: any) => {
                                                        return (
                                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                        )
                                                    })}
                                                </Form.Select>
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="district">
                                            <Form.Label column sm="2">Район <span
                                                className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="text" autoComplete={"off"}
                                                              value={district}
                                                              onChange={(e) => {
                                                                  setDistrict(e.target.value)
                                                              }}
                                                              required={true}

                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="address">
                                            <Form.Label column sm="2">Адрес <span
                                                className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="text" autoComplete={"off"}
                                                              value={address}
                                                              onChange={(e) => {
                                                                  setAddress(e.target.value)
                                                              }}
                                                              required={true}
                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>
                                        <Form.Group as={Row} className="mb-3" controlId="name">
                                            <Form.Label column sm="2">Название <span className={"text-danger"}>*</span></Form.Label>
                                            <Col sm="10">
                                                <Form.Control type="text" autoComplete={"off"}
                                                              value={name}
                                                              onChange={(e) => {
                                                                  setName(e.target.value)
                                                              }}
                                                              required={true}
                                                />
                                                <Form.Control.Feedback type="invalid">Поле обязательно для
                                                    заполнения</Form.Control.Feedback>
                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="comment">
                                            <Form.Label column sm="2">Фотографии (до 10)</Form.Label>
                                            <Col sm="10">
                                                {errorFiles &&
                                                    <Alert variant={"danger"} dismissible={true}>{errorFiles}</Alert>}
                                                <Form.Control type="file" multiple={true} accept={"image/*"}
                                                              onChange={handleSelectFile}
                                                />
                                                <Form.Text className="text-muted ps-2">Можно выбрать несколько
                                                    файлов</Form.Text>
                                                {object.photos
                                                    ?
                                                    <div className={"d-flex gap-3 my-3"}>
                                                        {object.photos && object.photos.map((photo: any) => {
                                                            const srcUrl = import.meta.env.MODE === 'development' ? import.meta.env.VITE_SERVER_URL_DEV :
                                                                "/";
                                                            return (
                                                                <div key={photo.id}
                                                                     className={"d-flex ai-center flex-column gap-3"}>
                                                                    <img alt={""} height={100}
                                                                         src={`${srcUrl}${photo.path}`}/>
                                                                    <FaRegTrashCan title={"Удалить фото"} size={16}
                                                                                   className={"pointer"}
                                                                                   onClick={() => handleDeleteExistingImage(photo.id)}
                                                                    />
                                                                </div>
                                                            )
                                                        })}
                                                    </div>
                                                    :
                                                    <i className={"text-muted fs-11"}>Не указан</i>
                                                }
                                            </Col>
                                        </Form.Group>

                                        <Form.Group as={Row} className="mb-3" controlId="comment">
                                            <Form.Label column sm="2">Комментарий</Form.Label>
                                            <Col sm="10">
                                                <Form.Control as="textarea" autoComplete={"off"} rows={4}
                                                              value={comment}
                                                              onChange={(e) => {
                                                                  setComment(e.target.value)
                                                              }}
                                                />
                                            </Col>
                                        </Form.Group>
                                    </>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                {object.action !== "DELETE" && (<Button variant="warning" type="submit">Изменить</Button>)}
                                <Button variant="secondary" onClick={handleHideModal}>
                                    {object.action === "DELETE" || success ? "Закрыть" : "Отмена"}
                                </Button>
                            </Modal.Footer>
                        </Form>
                        :
                        <>
                            <Modal.Body>
                                <Alert variant={"warning"} dismissible={true}>Объект не найден</Alert>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleHideModal}>Отмена</Button>
                            </Modal.Footer>
                        </>
                    }
                </>
            }
        </Modal>
    );
};

export default ObjectModalEdite;